import { useState, useEffect } from 'react';
import { env } from '@/config/env';
import { normalizeUserMenuItems } from '@/utils/route-utils';
import type { RemoteMountOptionsBase, RemoteUserMenuItem } from '@platform-ui/remote-types';
import { authService } from '@/auth';

/**
 * Shape of a federated module that exports user menu items.
 */
type UserMenuItemsModule = {
    getRemoteUserMenuItems?: (options: RemoteMountOptionsBase) => Promise<RemoteUserMenuItem[]> | RemoteUserMenuItem[];
    default?: RemoteUserMenuItem[];
};

/**
 * Extracts user menu items from a federated module result.
 */
async function extractUserMenuItems(moduleResult: unknown, mountOptions: RemoteMountOptionsBase): Promise<RemoteUserMenuItem[]> {
    if (Array.isArray(moduleResult)) {
        return moduleResult;
    }

    const mod = moduleResult as UserMenuItemsModule;

    if (typeof mod.getRemoteUserMenuItems === 'function') {
        return await mod.getRemoteUserMenuItems(mountOptions);
    }

    if (mod.default) {
        return mod.default;
    }

    return [];
}

/**
 * Hook to load all user menu items from all configured apps.
 * Each remote app's getRemoteUserMenuItems is async and handles its own permission fetching.
 *
 * @param accountId - Stable account identifier to trigger reload on auth changes
 * @param isAuthenticated - Whether the user is authenticated
 * @returns Array of all user menu items from all apps
 */
export function useAllUserMenuItems(accountId: string | null, isAuthenticated: boolean) {
    const [userMenuItems, setUserMenuItems] = useState<RemoteUserMenuItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;

        const loadAllUserMenuItems = async () => {
            const allItems: RemoteUserMenuItem[] = [];
            const appsWithUserMenu = env.apps.filter(config => config.remoteUserMenuItems);

            const loadPromises = appsWithUserMenu.map(async config => {
                const basePath = config.basePath || '/';

                try {
                    if (!config.importRemoteUserMenuItems) {
                        throw new Error(`No import function for: ${config.remoteUserMenuItems}`);
                    }

                    const moduleResult = await config.importRemoteUserMenuItems();

                    const mountOptions: RemoteMountOptionsBase = {
                        basePath: config.basePath,
                        federatedAuth: authService.createFederatedAuth(),
                        location: window.location.href
                    };

                    const raw = await extractUserMenuItems(moduleResult, mountOptions);
                    const normalized = normalizeUserMenuItems(raw, basePath);

                    allItems.push(...normalized);
                } catch (err) {
                    console.error(`Failed to load user menu items for ${config.appName}:`, err);
                }
            });

            await Promise.all(loadPromises);

            if (!cancelled) {
                setUserMenuItems(allItems);
                setIsLoading(false);
            }
        };

        loadAllUserMenuItems();

        return () => {
            cancelled = true;
        };
    }, [accountId, isAuthenticated]);

    return { userMenuItems, isLoading };
}
